import { memo, useEffect, useMemo, useRef } from 'react'
import { Transition } from 'react-transition-group'
import { ContentBlock, EditorState } from 'draft-js'
import Editor from '@draft-js-plugins/editor'
import { stateToHTML } from 'draft-js-export-html'
import { stateFromHTML } from 'draft-js-import-html'
import { Colors } from 'cio-design-library/design-tokens'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { clampNumber } from 'builder/utils/clampNumber'
import { useForwardedRef } from 'builder/hooks/useForwardedRef'
import {
  AIGeneratedDetails,
  AnimationWrapper,
  Bar,
  EditorWrapper,
  Group,
  Label,
  RelativeDiv,
  TextArea,
} from './styles'
import RichTextToolbar from './RichTextToolbar'
import createSpellcheckPlugin from './spellCheckPlugin'
import { findWithRegex } from './spellCheckPlugin/searchMistake'
import TextAreaProgress from './TextAreaProgress'
import TextAreaProgressRange from './TextAreaProgressRange'
import { DecoratorCallback, IncorrectWord, TextAreaProps } from './types'
import { useRichTextAreaActions } from './useRichTextAreaActions'
import createLinkPlugin from './linkPlugin'
import createListPlugin from './listPlugin'
import SpellCheckDropdown from './spellCheckPlugin/SpellCheckDropdown'

const linkPlugin = createLinkPlugin()
const listPlugin = createListPlugin()

const RichTextArea = (props: TextAreaProps) => {
  const {
    height = 200,
    disableToolbar,
    label = '',
    locale = 'en',
    boldButtonDisable = false,
    linkButtonDisable = false,
    italicButtonDisable = false,
    placeholder = '',
    recommendedLength,
    ranges,
    recommendedRangeLabel = '',
    isAnimating,
    value = '',
    onTextLengthChange,
    instanceRef,
    additionalToolbarActions,
    containerRef,
  } = props

  const {
    animationPlaceholderRef,
    editorId,
    editorRef,
    editorState,
    isFocused,
    isSpellCheckerEnabled,
    isSpellCheckerPending,
    isSpellingChecked,
    textLength,
    shouldHidePlaceholder,
    focusEditor,
    handleChange,
    handleToggleInline,
    handleToggleBlock,
    handleToggleSpellChecker,
    handleFocus,
    handleBlur,
    handleKeyBinding,
    handleKeyCommand,
    supportsLocale,
    setEditorState,
    addPhrase,
    replacePhrase,
    getEditorText,
  } = useRichTextAreaActions(props)
  const transitionRef = useRef(null)
  const editorContainerRef = useForwardedRef(containerRef)

  const highlightWordsStrategy = (contentBlock: ContentBlock, callback: DecoratorCallback) => {
    const ignoredWordsFromStorage = localStorage.getItem('SPELL_CHECKER_IGNORED_WORDS')
    const incorrectWordsFromStorage = localStorage.getItem('SPELL_CHECKER_INCORRECT_WORDS')
    const editorsWithSpellCheckFromStorage = localStorage.getItem('EDITORS_WITH_SPELL_CHECK')

    const ignoredWords = new Set(ignoredWordsFromStorage ? JSON.parse(ignoredWordsFromStorage) : [])
    const incorrectWords: IncorrectWord[] = incorrectWordsFromStorage
      ? JSON.parse(incorrectWordsFromStorage)
      : []
    const editorsWithSpellCheck: string[] = editorsWithSpellCheckFromStorage
      ? JSON.parse(editorsWithSpellCheckFromStorage)
      : []
    // Use local storage to disable the highlighting of the incorrect words
    if (!editorsWithSpellCheck.includes(editorId.current)) return
    incorrectWords.forEach(item => {
      if (ignoredWords.has(item.word)) return
      const WRONG_SPELLING_REGEXP = new RegExp(item.word, 'g')
      findWithRegex(WRONG_SPELLING_REGEXP, contentBlock, callback)
    })
  }
  const spellCheckPlugin = createSpellcheckPlugin(highlightWordsStrategy)

  const { isPhone, isTablet } = useMediaQueries()

  const isSpellCheckerAvailable = !(isPhone || isTablet)

  const showRecommendedLengthProgressBar = recommendedLength && !ranges
  const showRecommendedRangeProgressBar = !recommendedLength && ranges

  const barScale = useMemo(() => {
    if (textLength && recommendedLength) {
      return clampNumber(textLength / recommendedLength, 0, 1)
    } else if (textLength && ranges) {
      return clampNumber(textLength / ranges.good.start, 0, 1)
    }

    return 1
  }, [ranges, recommendedLength, textLength])

  const barColor = useMemo(() => {
    if (textLength === 0) return Colors.Blue50

    if (recommendedLength) {
      if (textLength / recommendedLength >= 1) return Colors.Green50
      if (textLength / recommendedLength > 0.33) return Colors.Amber40
      return Colors.Orange40
    } else if (ranges) {
      const { good, bad } = ranges
      if (textLength >= bad.start && textLength <= bad.end) return Colors.Red40
      if (textLength >= good.start && textLength <= good.end) return Colors.Green40
      return Colors.Amber40
    }

    return Colors.Blue50
  }, [ranges, recommendedLength, textLength])

  // sets new value if it is updated from outside of component
  useEffect(() => {
    const currentValue = stateToHTML(editorState.getCurrentContent())

    if (value && value !== currentValue) {
      const contentState = stateFromHTML(value)
      const newState = EditorState.createWithContent(contentState)
      setEditorState(newState)
    }

    // needs to react only on value change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  useEffect(() => {
    onTextLengthChange?.(textLength)
  }, [onTextLengthChange, textLength])

  useEffect(() => {
    if (instanceRef) {
      instanceRef.current = {
        focusEditor,
        addPhrase,
        replacePhrase,
        getEditorText,
      }
    }
  }, [instanceRef, focusEditor, addPhrase, replacePhrase, getEditorText])

  return (
    <Group>
      <Label> {label} </Label>
      <TextArea {...{ height }}>
        {!disableToolbar && (
          <RichTextToolbar
            onChange={handleChange}
            editorState={editorState}
            onToggleInline={handleToggleInline}
            onToggleBlock={handleToggleBlock}
            focusEditor={focusEditor}
            onToggleSpellChecker={handleToggleSpellChecker}
            isSpellCheckerEnabled={isSpellCheckerEnabled}
            isSpellCheckerVisible={
              isSpellCheckerAvailable && supportsLocale(locale) && isSpellingChecked
            }
            isSpellCheckerPending={isSpellCheckerPending}
            boldButtonDisable={boldButtonDisable}
            italicButtonDisable={italicButtonDisable}
            linkButtonDisable={linkButtonDisable}
            additionalToolbarActions={additionalToolbarActions}
          />
        )}

        <RelativeDiv>
          {isAnimating && (
            <AnimationWrapper>
              <AIGeneratedDetails ref={animationPlaceholderRef} />
            </AnimationWrapper>
          )}
          <EditorWrapper isAnimated={!!isAnimating} ref={editorContainerRef}>
            <Editor
              placeholder={shouldHidePlaceholder ? undefined : placeholder}
              editorState={editorState}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              keyBindingFn={handleKeyBinding}
              handleKeyCommand={handleKeyCommand}
              plugins={[spellCheckPlugin, linkPlugin, listPlugin]}
              ref={editorRef}
            />
            <SpellCheckDropdown
              {...{
                editorState,
                handleChange,
                setEditorState,
                spellCheckEmitter: spellCheckPlugin.emitter,
              }}
            />
          </EditorWrapper>
        </RelativeDiv>

        <Transition in={isFocused} timeout={100} nodeRef={transitionRef}>
          {state => <Bar in={state} scale={barScale} bgColor={barColor} ref={transitionRef} />}
        </Transition>
      </TextArea>

      {showRecommendedLengthProgressBar && (
        <TextAreaProgress length={textLength} recommendedLength={recommendedLength} />
      )}

      {showRecommendedRangeProgressBar && (
        <TextAreaProgressRange length={textLength} ranges={ranges} label={recommendedRangeLabel} />
      )}
    </Group>
  )
}

export default memo(RichTextArea)
